import React, { useState } from 'react';
import { Check, Loader } from 'lucide-react';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

import { RootState } from '../../../reducers';
import logger from '../../../lib/logger';
import { requestStatusType } from '../../../types';
import { setupPayments } from '../../../services/subscriptionServices';
import { planPricesPerMonth } from '../../../constants/prices';
import { config } from '../../../../config';
import { createOrUpdateSubscription } from '../thunk';
import { setAlertMessage } from '../../dashboard/dashboardSlice';
import { pushModal } from '../../globalModals/globalModalsSlice';

const { stripePublishableKey } = config;
const stripePromise = loadStripe(stripePublishableKey, {
  betas: ['custom_checkout_beta_2'],
});

// Helper function to get relative dates
const getRelativeDate = (daysFromNow: number): string => {
  const date = new Date();
  date.setDate(date.getDate() + daysFromNow);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const PaymentSection = ({ onClose }: { onClose: () => void }) => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [upgradeStatus, setUpgradeStatus] = useState<requestStatusType>('idle');

  const { team, settings } = useSelector((state: RootState) => state);
  const teamId = team.selectedTeam?.teamId || team.teams[0].teamId;
  const { email } = settings.user;

  const yearlyPrice = planPricesPerMonth.GROWTH.annual * 12; // Define yearlyPrice here

  const handleError = (error: any) => {
    logger.error(error);
    setUpgradeStatus('failed');
  };

  const handleStartTrial = async () => {
    if (!stripe || !elements) {
      logger.error(t('upgrade.freeTrial.errors.stripeError'));
      return;
    }

    setUpgradeStatus('pending');

    const { error: submitError } = await elements.submit();
    if (submitError) {
      handleError(submitError);
      return;
    }

    try {
      // Collect and verify the payment method
      const { clientSecret } = await setupPayments({
        // userId is implicitly passed with the jwt token
        teamId,
        metadata: {
          toltReferral: window.tolt_referral,
        },
      });

      const { error } = await stripe.confirmSetup({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${config.appBaseUrl}`,
        },
        redirect: 'if_required',
      });

      if (error) {
        throw error;
      }

      const result = await dispatch(
        createOrUpdateSubscription({
          teamId,
          plan: 'GROWTH',
          term: 'annual',
          totalAmount: yearlyPrice,
          // Backend checks this and assigns the number of trial days
          trialLabel: '3 days free growth annual trial',
          metadata: {
            toltReferral: window.tolt_referral,
          },
        }),
      );

      if (createOrUpdateSubscription.rejected.match(result as never)) {
        onClose();
        const { message = '' } = (result as any).error || {};
        throw new Error(message);
      }

      onClose();
      setUpgradeStatus('succeeded');
      dispatch(
        pushModal({
          type: 'upgradeSuccess',
        }),
      );
    } catch (error) {
      setUpgradeStatus('failed');
      dispatch(
        setAlertMessage({
          type: 'error',
          message: t('upgrade.freeTrial.errors.upgradeError', {
            message: error.message,
          }),
        }),
      );
      handleError(error);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <PaymentElement
          id="stripe-payment-element"
          options={{
            defaultValues: {
              billingDetails: {
                email,
              },
            },
          }}
        />
      </div>

      <div className="flex items-center justify-center gap-3 py-1">
        <Check className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
        <span className="text-base font-semibold">No payment today</span>
      </div>

      <Button
        type="button"
        variant="default"
        className="w-full h-12 text-base font-bold"
        onClick={handleStartTrial}
        disabled={upgradeStatus === 'pending'}
      >
        {upgradeStatus === 'pending' ? (
          <>
            <Loader className="mr-2 h-5 w-5 animate-spin" />
            {t('upgrade.freeTrial.processing')}
          </>
        ) : (
          t('upgrade.freeTrial.getFreeTrial')
        )}
      </Button>

      <div className="text-center text-sm text-muted-foreground opacity-60">
        Then ${yearlyPrice}/year (${planPricesPerMonth.GROWTH.annual}/mo)
        starting {getRelativeDate(3)}
      </div>
    </div>
  );
};

interface FreeTrialProps {
  onClose: () => void;
}

const FreeTrial: React.FC<FreeTrialProps> = ({ onClose }) => {
  const getConversionMessages = (): string[] => {
    return [
      'Get access to all Growth features',
      `Cancel before ${getRelativeDate(3)}, pay nothing`,
    ];
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        className="max-w-md w-full max-h-[90vh] overflow-y-auto"
        data-testid="free-trial-modal"
      >
        {/* Header Section */}
        <DialogHeader className="pb-6">
          <DialogTitle className="text-2xl font-bold">
            Start your FREE 3 day trial
          </DialogTitle>
        </DialogHeader>

        {/* Content Section */}
        <div className="space-y-6">
          {/* Conversion Messaging */}
          <div className="space-y-4">
            {getConversionMessages().map((text, index) => (
              <div key={index} className="flex items-start gap-3">
                <Check className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                <span className="text-base leading-relaxed flex-1">{text}</span>
              </div>
            ))}
          </div>

          {/* Payment section */}
          <Elements
            stripe={stripePromise}
            options={{
              mode: 'setup',
              currency: 'usd',
              setupFutureUsage: 'off_session',
            }}
          >
            <PaymentSection onClose={onClose} />
          </Elements>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FreeTrial;
